import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/apiClient';
import { fmtTs } from '../lib/globalTz';
import { INCIDENT_EXECUTION_UPDATED_EVENT } from '../lib/incidentExecutionEvents';

export interface IncidentExecutionDetail {
  id: string;
  actionType: string;
  requestedBy: string;
  executedBy: string;
  executionIdentity: 'analyst' | 'service-principal';
  executionPath: string;
  method: 'delegated-arm' | 'service-principal-arm' | 'service-principal-customer-graph';
  fallbackUsed: boolean;
  providerName?: string;
  providerIncidentId?: string;
  incidentNumber?: string;
  correlationId: string;
  verified: boolean;
  timestamp: string;
}

interface ExecutionResponse {
  incidentId: string;
  customerId: string;
  executions: IncidentExecutionDetail[];
}

interface Props {
  incidentId: string;
  customerId?: string;
}

const ACTION_LABEL: Record<string, string> = {
  close: 'Closed incident',
  status: 'Changed status',
  severity: 'Changed severity',
  labels: 'Updated labels',
  assign: 'Changed assignment',
  comment: 'Added comment',
};

function identityBadge(execution: IncidentExecutionDetail) {
  return execution.executionIdentity === 'analyst'
    ? 'Analyst execution'
    : 'SPN fallback';
}

export function IncidentExecutionDetailsPanel({ incidentId, customerId }: Props) {
  const [executions, setExecutions] = useState<IncidentExecutionDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!customerId || !incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = `?customerId=${encodeURIComponent(customerId)}`;
      const response = await apiFetch<ExecutionResponse>(`/incidents/${encodeURIComponent(incidentId)}/execution-details${qs}`);
      setExecutions(Array.isArray(response.executions) ? response.executions : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [customerId, incidentId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ incidentId?: string }>).detail;
      if (!detail?.incidentId || detail.incidentId === incidentId) void load();
    };
    window.addEventListener(INCIDENT_EXECUTION_UPDATED_EVENT, handler);
    return () => window.removeEventListener(INCIDENT_EXECUTION_UPDATED_EVENT, handler);
  }, [incidentId, load]);

  const latest = executions[0];
  const history = useMemo(() => executions.slice(0, expanded ? 10 : 1), [executions, expanded]);

  return (
    <div className="mb-4 rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-border bg-muted/35">
        <div>
          <div className="text-xs font-semibold text-foreground">Execution Details</div>
          <div className="text-[10px] text-muted-foreground">Who requested and who actually executed IncidentOps changes.</div>
        </div>
        <div className="flex items-center gap-2">
          {latest && (
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${latest.fallbackUsed ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}`}>
              {identityBadge(latest)}
            </span>
          )}
          <button type="button" onClick={() => void load()} disabled={loading} className="rounded border border-border px-2 py-1 text-[10px] font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="px-3 py-3 text-xs text-red-600 dark:text-red-300">Could not load execution history: {error}</div>
      ) : executions.length === 0 ? (
        <div className="px-3 py-3 text-xs text-muted-foreground">No KloudSecure execution records have been captured for this incident yet.</div>
      ) : (
        <div className="divide-y divide-border">
          {history.map((execution) => (
            <div key={execution.id} className="px-3 py-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="text-xs font-semibold text-foreground">{ACTION_LABEL[execution.actionType] ?? execution.actionType}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtTs(execution.timestamp)}</div>
                </div>
                <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${execution.verified ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
                  {execution.verified ? 'Verified' : 'Unverified'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                <div><span className="text-muted-foreground">Requested by:</span> <span className="text-foreground">{execution.requestedBy}</span></div>
                <div><span className="text-muted-foreground">Executed by:</span> <span className="text-foreground">{execution.executedBy}</span></div>
                <div><span className="text-muted-foreground">Execution path:</span> <span className="text-foreground">{execution.executionPath}</span></div>
                <div><span className="text-muted-foreground">Fallback:</span> <span className="text-foreground">{execution.fallbackUsed ? 'Service Principal' : 'Not used'}</span></div>
                <div><span className="text-muted-foreground">Provider:</span> <span className="text-foreground">{execution.providerName || 'Microsoft Sentinel'}</span></div>
                <div><span className="text-muted-foreground">Incident IDs:</span> <span className="font-mono text-foreground">S:{execution.incidentNumber || '—'} / P:{execution.providerIncidentId || '—'}</span></div>
              </div>
              {execution.correlationId && (
                <div className="mt-2 text-[10px] font-mono text-muted-foreground break-all">Correlation: {execution.correlationId}</div>
              )}
            </div>
          ))}
          {executions.length > 1 && (
            <div className="px-3 py-2">
              <button type="button" onClick={() => setExpanded(v => !v)} className="text-[11px] font-semibold text-primary hover:underline">
                {expanded ? 'Show latest only' : `Show execution history (${executions.length})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
